import { createRoot } from 'react-dom/client'
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { NotFound } from "./pages/NotFound";
import { HomePage } from './pages/HomePage';
import { AppContext, AppContextProvider } from '../contexts/AppContext';
import { Header } from './components/Header';
import { Toaster } from 'react-hot-toast';
import { PositionsList } from './pages/PositionsList';
import { ProtectedRoute } from './components/Routes/ProtectedRoute';
import { PersonalDetails } from './pages/PersonalDetails';
import { AccountSecurity } from './pages/AccountSecurity';
import { JobAlerts } from './pages/JobAlerts';
import { EmailLog } from './pages/EmailLog';
import { Register } from './pages/Register';
import { AnonRoute } from './components/Routes/AnonRoute';
import { BreadcrumbBar } from './components/BreadcrumbBar';
import { RouteContextProvider } from '../contexts/RoutesContext';
import { ForgotPassword } from './pages/ForgotPassword';
import { ChangePassword } from './pages/ChangePassword';
import { BackToTopButton } from './components/Shared/BackToTopButton';
import { FeaturedRoute } from './components/Routes/FeaturedRoute';
import { useContext } from 'react';
import { CandidateStateRoute } from './components/Routes/CandidateStateRoute';
import { LeftHandMenu } from './components/LeftHandMenu';
import { PositionDetails } from './pages/PositionDetails';
import ErrorBoundary from './components/Shared/ErrorBoundary';
import { JobAlertsUnsubscribe } from './pages/JobAlertsUnsubscribe';
import { Speculative } from './pages/Speculative';
import { ManagedCandidate } from './pages/ManagedCandidate';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { AdminAndAgencyOnlyRoute } from './components/Shared/AdminAndAgencyOnlyRoute';
import { PrivacyPolicyUpdate } from './pages/PrivacyPolicyUpdate';
import { PositionsListIntranet } from './pages/PositionsListIntranet';
import { IntranetQuickApply } from './pages/IntranetQuickApply';
import { PositionClosed } from './pages/PositionClosed';
import { CandidateOnlyRoute } from './components/Routes/CandidateOnlyRoute';
import { UserType } from './Variables';
import { ApplicationSubmitted } from './pages/ApplicationSubmitted';

const root = document.getElementById("root");
let apiRoot = document.getElementById("appapi_url")?.getAttribute('data-url') || "/appapi/";
let firmName = document.getElementById("app_firmname")?.getAttribute('data-fn') || "";
let appRoot = document.getElementById("app_url")?.getAttribute('data-url') || "/app/";
let systemRoot = document.getElementById("content_url")?.getAttribute('data-url') || "/";
let styleContainer = document.getElementById("app_styles");
let carousel = styleContainer?.getAttribute('data-carousel');
let banner = styleContainer?.getAttribute('data-banner');
let bannerVer = styleContainer?.getAttribute('data-bannerver');
let styleColourPrimary = styleContainer?.getAttribute('data-pc');
let styleColourSecondary = styleContainer?.getAttribute('data-sc');
let styleColourAlternative = styleContainer?.getAttribute('data-ac');
let styleColourAllHires = styleContainer?.getAttribute('data-ahc');
let positionsLabel = styleContainer?.getAttribute('data-pl');

let basename = appRoot;
if (basename.endsWith('/')) {
    basename = basename.substr(0, basename.length - 1);
}

const rootRenderer = createRoot(root);

rootRenderer.render(
    <AppContextProvider
        apiRoot={apiRoot}
        appRoot={appRoot}
        systemRoot={systemRoot}
        redesign_Carousel={carousel.toLocaleLowerCase() == "true"}
        redesign_BannerOnly={banner.toLocaleLowerCase() == "true"}
        redesign_BannerVer={bannerVer}
        styleColourPrimary={styleColourPrimary}
        styleColourSecondary={styleColourSecondary}
        styleColourAlternative={styleColourAlternative}
        styleColourAllHires={styleColourAllHires}
        positionsLabel={positionsLabel}
        firmName={firmName}>
        <RouteContextProvider>
            <Router basename={basename}>
                <Header />
                <BreadcrumbBar />
                <div className='container-xxl central-container pb-3'>
                    <Routes>
                        <Route path="/" element={<CandidateStateRoute doPersonalDetailsCheck={true}><PositionsList /></CandidateStateRoute>} />
                        <Route path="/Speculative" element={<FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.JobBoard_Speculatives;
                                }
                                return true;
                            }}><Speculative /></FeaturedRoute>} />
                        <Route path="/Intranet" element={<FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.IsIntranetDisplay;
                                }
                                return false;
                            }}><PositionsListIntranet /></FeaturedRoute>} />
                        <Route path="/Intranet/PositionDetails" element={<FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.IsIntranetDisplay;
                                }
                                return false;
                            }}><PositionDetails intra={true} /></FeaturedRoute>} />
                        <Route path="/Intranet/Apply" element={<FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.IsIntranetDisplay;
                                }
                                return false;
                            }}><IntranetQuickApply /></FeaturedRoute>} />
                        <Route path="/PositionDetails" element={<ErrorBoundary><PositionDetails intra={false} /></ErrorBoundary>} />
                        <Route path="/PersonalDetails" element={<ProtectedRoute><CandidateStateRoute doPersonalDetailsCheck={false}><PersonalDetails /></CandidateStateRoute></ProtectedRoute>} />
                        <Route path="/AccountSecurity" element={<ProtectedRoute><FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.MFA_Active;
                                }
                                return true;
                            }}><CandidateOnlyRoute><CandidateStateRoute doPersonalDetailsCheck={true}><AccountSecurity /></CandidateStateRoute></CandidateOnlyRoute></FeaturedRoute></ProtectedRoute>} />
                        <Route path="/Home" element={<ProtectedRoute><CandidateStateRoute doPersonalDetailsCheck={true}><HomePage /></CandidateStateRoute></ProtectedRoute>} />
                        <Route path="/EmailLog" element={<ProtectedRoute><CandidateOnlyRoute><CandidateStateRoute doPersonalDetailsCheck={true}><ErrorBoundary><EmailLog /></ErrorBoundary></CandidateStateRoute></CandidateOnlyRoute></ProtectedRoute>} />
                        <Route path="/JobAlerts" element={<ProtectedRoute><CandidateStateRoute doPersonalDetailsCheck={true}><FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);

                                if (!app.loading) {
                                    return app.baseFeatures.JobAlerts;
                                }
                                return false;
                            }}><JobAlerts /></FeaturedRoute></CandidateStateRoute></ProtectedRoute>} />
                        <Route path="/JobAlertsUnsubscribe" element={<FeaturedRoute
                            isLoading={() => {
                                const app = useContext(AppContext);
                                return app.loading;
                            }}
                            featureCheckLogic={() => {
                                const app = useContext(AppContext);
                                if (!app.loading) {
                                    return app.baseFeatures.JobAlerts;
                                }
                                return false;
                            }}><JobAlertsUnsubscribe /></FeaturedRoute>} />
                        <Route path="/Register" element={<AnonRoute UrlPartial='/PersonalDetails'><Register /></AnonRoute>} />
                        <Route path="/ManagedCandidate" element={<AdminAndAgencyOnlyRoute><ManagedCandidate /></AdminAndAgencyOnlyRoute>} />
                        <Route path="/ForgotPassword" element={<AnonRoute UrlPartial='/home'><ForgotPassword /></AnonRoute>} />
                        <Route path="/ChangePassword" element={<ChangePassword />} />
                        <Route path="/PositionClosed" element={<PositionClosed />} />
                        <Route path="/ApplicationSubmitted" element={<ApplicationSubmitted />} />
                        <Route path="/PrivacyPolicy" element={<ProtectedRoute><CandidateOnlyRoute>
                            <FeaturedRoute
                                isLoading={() => {
                                    const app = useContext(AppContext);
                                    return app.loading;
                                }}
                                featureCheckLogic={() => {
                                    const app = useContext(AppContext);
                                    if (!app.loading && app.userLoggedIn && app.loggedInUser.UserType == UserType.Candidate && app.loggedInUser.PrivacyPolicyUpdateRequired && !app.policyUpdateToDate) {
                                        return true;
                                    }
                                    return false;
                                }}>
                                <PrivacyPolicyUpdate />
                            </FeaturedRoute>
                        </CandidateOnlyRoute></ProtectedRoute>} />
                        <Route path="/EmailVerification" element={<EmailVerificationPage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </div>
                <BackToTopButton />
                <LeftHandMenu mobileOnly={true} />
            </Router>
        </RouteContextProvider>
        <Toaster />
    </AppContextProvider>
)